import { EventRecord, EventService } from "@/shared/domain/events";
import { tryBuildEventScopeLayout } from "@/shared/domain/events/eventScopeLayout";
import { eventQueryKeys } from "@/shared/domain/events/hooks";
import {
  EventScope,
  resolveEventScope,
} from "@/shared/domain/events/resolveEventScope";
import { TopicService } from "@/shared/domain/topics";
import { RobotoNotFoundException } from "@/shared/http";
import { RobotoUnauthorizedException } from "@/shared/http/exceptions";
import { Datasource } from "@/shared/state/visualization";

import { temporaryLayoutRecordFrom } from "./resolvers";
import {
  type ConfigSource,
  type EventSidebar,
  type VisualizerInitContext,
  type VisualizerNotice,
} from "./visualizerInit";

/**
 * The result of resolving the URL's `?event=`: the event record and the
 * scope its associations cover when the event loaded, or a marker for the
 * two ways loading it can fail that the view recovers from.
 */
export type EventResolution =
  | { status: "ok"; event: EventRecord; scope: EventScope }
  | { status: "not-found"; eventId: string }
  | { status: "unauthorized"; eventId: string };

/**
 * Fetch the event named in the URL and resolve its scope in one pass, so the
 * datasources, panels, sidebar, and notices it feeds all read from a single
 * fetch. Returns null when the URL names no event.
 *
 * A missing or forbidden event returns a marker rather than throwing: losing
 * the linked event must not tear down a workspace whose URL datasources the
 * user may be allowed to see. Unexpected failures still throw for the loader
 * to log.
 *
 * @throws Error when the event fetch fails for any reason other than a 404 or 403
 */
export async function resolveEvent(
  ctx: VisualizerInitContext,
): Promise<EventResolution | null> {
  const { eventId } = ctx;

  if (!eventId) {
    return null;
  }

  let event: EventRecord;
  try {
    event = await fetchEvent(ctx, eventId);
  } catch (error) {
    if (error instanceof RobotoNotFoundException) {
      return { status: "not-found", eventId };
    }
    if (error instanceof RobotoUnauthorizedException) {
      return { status: "unauthorized", eventId };
    }

    throw new Error(`Failed to resolve event ${eventId}`, { cause: error });
  }

  const topicService = new TopicService(ctx.httpClient);
  const scope = await resolveEventScope(event, topicService);

  return { status: "ok", event, scope };
}

/**
 * Fetch the event record without resolving its scope. The record is cached
 * under the key the workspace sidebar's event-details view reads, so that view
 * and a later `resolveEvent` for the same event reuse it instead of fetching
 * again. A failed fetch caches nothing, so the next call fetches again.
 *
 * @throws Error when the event fetch fails, rejecting with the fetch's own
 *   error (e.g. `RobotoNotFoundException`) unwrapped
 */
export function fetchEvent(
  ctx: VisualizerInitContext,
  eventId: string,
): Promise<EventRecord> {
  const eventService = new EventService(ctx.httpClient);
  return ctx.queryClient.ensureQueryData({
    queryKey: eventQueryKeys.detail(ctx.orgId, eventId),
    queryFn: ({ signal }) =>
      eventService.getEventById(eventId, { abortSignal: signal }),
  });
}

/** The event's id, whichever way it resolved. */
export function eventIdOf(resolution: EventResolution): string {
  return resolution.status === "ok"
    ? resolution.event.event_id
    : resolution.eventId;
}

/**
 * A layout config source built from the event's message-path scope, or null
 * when the event failed to load or its scope produces no panels. The event
 * supplies panels only when no higher-priority source does.
 */
export function resolveEventPanels(
  resolution: EventResolution | null,
): ConfigSource | null {
  if (resolution?.status !== "ok") {
    return null;
  }

  const layout = tryBuildEventScopeLayout(resolution.scope.scopedMessagePaths);
  if (!layout) {
    return null;
  }

  return {
    type: "layout",
    value: temporaryLayoutRecordFrom(layout),
    origin: "event",
  };
}

/**
 * The datasources the event opens, ready to merge with the URL's own: a
 * session event's session, or the datasets and files a dataset event's
 * associations resolve to. Empty when the event failed to load.
 */
export function eventDatasources(
  resolution: EventResolution | null,
): Datasource[] {
  if (resolution?.status !== "ok") {
    return [];
  }

  const sessionId = resolution.event.session_id;
  if (sessionId !== undefined) {
    return [{ type: "session", value: { sessionId } }];
  }

  const { datasetIds, fileIds } = resolution.scope;
  return [
    ...datasetIds.map((datasetId) => ({
      type: "dataset" as const,
      value: { datasetId },
    })),
    ...fileIds.map((fileId) => ({
      type: "file" as const,
      value: { fileId },
    })),
  ];
}

/**
 * Which sidebar view the `?event=` link opens: the event's details when it
 * loaded, the events list when it could not (so the user is not stuck on a
 * broken detail view), or nothing when the URL named no event.
 */
export function eventSidebarFor(
  resolution: EventResolution | null,
): EventSidebar | undefined {
  if (!resolution) {
    return undefined;
  }

  if (resolution.status === "ok") {
    return { eventId: resolution.event.event_id, view: "details" };
  }

  return { eventId: resolution.eventId, view: "events-list" };
}

/**
 * Banners raised for the event's own state: not found, forbidden, or a dataset
 * event loaded with no reachable associated data. Empty when the event loaded
 * with data, the event is a session event (it opens its session, and has no
 * associations to resolve), or the URL named no event.
 */
export function eventNotices(
  resolution: EventResolution | null,
): VisualizerNotice[] {
  if (!resolution) {
    return [];
  }

  if (resolution.status === "not-found") {
    return [
      {
        name: "EventNotFoundError",
        message: `Event '${resolution.eventId}' not found.`,
        severity: "error",
      },
    ];
  }

  if (resolution.status === "unauthorized") {
    // Access to an event comes from its datasets and files — events can't be
    // shared individually — so the remedy names those, not the event.
    return [
      {
        name: "EventUnauthorizedError",
        title: "Unauthorized",
        message: `Viewing event '${resolution.eventId}' requires access to its underlying data.`,
        severity: "error",
      },
    ];
  }

  if (resolution.event.session_id !== undefined) {
    return [];
  }

  const { datasetIds, fileIds } = resolution.scope;
  if (datasetIds.length === 0 && fileIds.length === 0) {
    return [
      {
        name: "EventScopeUnresolvedError",
        message: `Data underlying event '${resolution.event.event_id}' not found.`,
        severity: "error",
      },
    ];
  }

  return [];
}
